import React, { useRef } from 'react';
import { useFrame } from '@react-three/fiber';

const AccountingShapes = ({ isDarkMode = false }) => {
  const shapesRef = useRef([]);

  // Accounting and tax related shapes (simplified - no text)
  const accountingShapes = [
    // Dollar sign rings
    {
      type: 'dollarRing',
      position: [-5, 2, -8],
      scale: 0.3,
      rotation: [0, 0, 0],
      color: isDarkMode ? '#A37A00' : '#8A6800'
    },
    {
      type: 'dollarRing',
      position: [6, -1, -10],
      scale: 0.25,
      rotation: [0, Math.PI/4, 0],
      color: isDarkMode ? '#B88A00' : '#9C7200'
    },
    
    // Coins
    {
      type: 'coin',
      position: [-6, -4, -11],
      scale: 0.12,
      rotation: [0, 0, 0],
      color: isDarkMode ? '#B88A00' : '#D4A017'
    },
    {
      type: 'coin',
      position: [2, 6, -19],
      scale: 0.1,
      rotation: [0, Math.PI/2, 0],
      color: isDarkMode ? '#D4A017' : '#B88A00'
    },
    
    // Calculator
    {
      type: 'calculator',
      position: [5, 5, -22],
      scale: 0.35,
      rotation: [0, Math.PI/4, 0],
      color: isDarkMode ? '#2d3748' : '#4a5568'
    },
    
    // Documents
    {
      type: 'document',
      position: [0, 5, -20],
      scale: 0.18,
      rotation: [0, Math.PI, 0],
      color: isDarkMode ? '#2d3748' : '#4a5568'
    },
    
    // Graph bars
    {
      type: 'barChart',
      position: [7, 2, -18],
      scale: 0.25,
      rotation: [0, Math.PI/3, 0],
      color: isDarkMode ? '#A37A00' : '#8A6800'
    }
  ];

  useFrame((state) => {
    shapesRef.current.forEach((shape, i) => {
      if (shape) {
        shape.position.y = accountingShapes[i].position[1] + 
          Math.sin(state.clock.elapsedTime * 0.5 + i) * 0.5;
        shape.rotation.y += 0.005;
        shape.rotation.x += 0.002;
      }
    });
  });

  const renderShape = (shape, index) => {
    const { type, position, scale, rotation, color } = shape;

    switch(type) {
      case 'dollarRing':
        return (
          <mesh
            key={index}
            ref={(el) => (shapesRef.current[index] = el)}
            position={position}
            rotation={rotation}
            scale={scale}
          >
            <torusGeometry args={[0.5, 0.1, 16, 32]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={isDarkMode ? 0.3 : 0.15}
              metalness={0.6}
              roughness={0.3}
            />
          </mesh>
        );

      case 'coin':
        return (
          <mesh
            key={index}
            ref={(el) => (shapesRef.current[index] = el)}
            position={position}
            rotation={rotation}
            scale={scale}
          >
            <cylinderGeometry args={[0.5, 0.5, 0.05, 32]} />
            <meshStandardMaterial
              color={color}
              emissive={color}
              emissiveIntensity={isDarkMode ? 0.4 : 0.2}
              metalness={0.8}
              roughness={0.2}
            />
          </mesh>
        );

      case 'calculator':
        return (
          <mesh
            key={index}
            ref={(el) => (shapesRef.current[index] = el)}
            position={position}
            rotation={rotation}
            scale={scale}
          >
            <boxGeometry args={[1.5, 2, 0.3]} />
            <meshStandardMaterial
              color={color}
              emissive={isDarkMode ? color : undefined}
              emissiveIntensity={isDarkMode ? 0.05 : 0}
              metalness={0.6}
              roughness={0.3}
            />
          </mesh>
        );

      case 'document':
        return (
          <mesh
            key={index}
            ref={(el) => (shapesRef.current[index] = el)}
            position={position}
            rotation={rotation}
            scale={scale}
          >
            <boxGeometry args={[0.8, 1.1, 0.05]} />
            <meshStandardMaterial
              color={color}
              emissive={isDarkMode ? color : undefined}
              emissiveIntensity={isDarkMode ? 0.05 : 0}
              metalness={0.3}
              roughness={0.6}
            />
          </mesh>
        );

      case 'barChart':
        return (
          <group
            key={index}
            ref={(el) => (shapesRef.current[index] = el)}
            position={position}
            rotation={rotation}
            scale={scale}
          >
            {/* Bar 1 */}
            <mesh position={[-0.3, 0.5, 0]}>
              <boxGeometry args={[0.2, 1, 0.2]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={isDarkMode ? 0.2 : 0.1}
              />
            </mesh>
            {/* Bar 2 */}
            <mesh position={[0, 0.7, 0]}>
              <boxGeometry args={[0.2, 1.4, 0.2]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={isDarkMode ? 0.25 : 0.12}
              />
            </mesh>
            {/* Bar 3 */}
            <mesh position={[0.3, 0.9, 0]}>
              <boxGeometry args={[0.2, 1.8, 0.2]} />
              <meshStandardMaterial
                color={color}
                emissive={color}
                emissiveIntensity={isDarkMode ? 0.3 : 0.15}
              />
            </mesh>
          </group>
        );

      default:
        return null;
    }
  };

  return (
    <group>
      {accountingShapes.map((shape, index) => renderShape(shape, index))}
    </group>
  );
};

export default AccountingShapes;